import React, { useState, useEffect } from 'react';
import { Button, Descriptions, Input, Typography, message, Modal, notification, Result, Switch } from 'antd';
import LoadingContainer from '../../common/components/LoadingContainer/LoadingContainer';
import { researchUI } from '../../api';
import { CommonApiFunctions, CommonConstant, ErrorMsg, NOTIFICATION_DURATION, UiURLs } from '../../common/constants';
import { MLApiRequestTypes, MLScoreLabels, MLScoreTexts, MLAppAction } from './constants';
import QuesCategoryBlock from './QuesCategoryBlock';
import { ML_Ques_Category_Map } from './MLScore';
import { COMMON_NUMBER, ERROR_MSG } from '../Helper/Constant';
import { ExportOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const MLScoreModal = ({ mlRow, visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [appData, setAppData] = useState(null);

    const [confirmLoading, setConfirmLoading] = useState(null);
    const [modalVisible, setModalVisible] = useState(visible);

    const [error, setError] = useState(false);
    const [comment, setComment] = useState(CommonConstant.EMPTY_STRING);

    const fetchAppData = async () => {
        setLoading(true);
        const result = await researchUI({
            function: CommonApiFunctions.ML_SCORE,
            reqType: MLApiRequestTypes.GET_ML_SCORE_APP_DATA,
            application_id: mlRow.application_id,
        });
        if (result.success) {
            setAppData(result.data);
            const ques = [];
            result.data.question.forEach((quesCat) => {
                quesCat.AppIndexQuestions.forEach((q) => {
                    ques.push(q.question_col_name);
                });
            });
        } else {
            setError(true);
            notification.error({
                message: result.msg ?? ErrorMsg.GET_APP_FAILED,
                description: result.msg,
                duration: NOTIFICATION_DURATION,
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchAppData();
    }, []);

    const onChangeComment = (e) => {
        setComment(e.target.value);
    };

    const onCloseModal = (isRefreshRequire = false) => {
        Object.keys(ML_Ques_Category_Map).forEach((key) => delete ML_Ques_Category_Map[key]);
        setModalVisible(false);
        onClose(isRefreshRequire);
    };

    const mlAppAction = async (action, data = null) => {
        setConfirmLoading(action);
        const result = await researchUI({
            function: CommonApiFunctions.ML_SCORE,
            reqType: MLApiRequestTypes.ML_SCORE_ACTION,
            id: mlRow.id,
            action,
            comment: comment,
            application_id: mlRow.application_id,
            ...data,
        });
        if (result.success === COMMON_NUMBER.API_SUCCESS) {
            onCloseModal(true);
        } else if (result.success === COMMON_NUMBER.VALIDATION_FAILED) {
            notification.error({
                message: ErrorMsg.VALIDATION_FAILED,
                description: <ValidationFailedNotification failedAttr={result.data} />,
                duration: 0,
            });
        } else {
            message.error(result.msg ?? ERROR_MSG.GENERAL_ERROR);
        }
        setConfirmLoading(null);
    };

    const onApprove = async () => {
        const skip_question_list = [];
        Object.values(ML_Ques_Category_Map).forEach((quesCat) => {
            Object.entries(quesCat).forEach(([key, value]) => {
                if (!value.checked) {
                    skip_question_list.push(key);
                }
            });
        });
        mlAppAction(MLAppAction.APPROVE, { skip_question_list });
    };
    const onReject = async () => {
        await mlAppAction(MLAppAction.REJECT);
    };

    return (
        <Modal
            title={CommonConstant.EMPTY_STRING}
            open={modalVisible}
            confirmLoading={confirmLoading}
            onCancel={() => onCloseModal(false)}
            width={'70%'}
            footer={
                loading || error
                    ? false
                    : [
                          <Button
                              key={'approve_ml_score'}
                              type='primary'
                              loading={confirmLoading === MLAppAction.APPROVE}
                              disabled={confirmLoading && confirmLoading !== MLAppAction.APPROVE}
                              onClick={onApprove}
                          >
                              {CommonConstant.APPROVE}
                          </Button>,
                          <Button
                              key={'reject_ml_score'}
                              type='primary'
                              loading={confirmLoading === MLAppAction.REJECT}
                              disabled={confirmLoading && confirmLoading !== MLAppAction.REJECT}
                              onClick={onReject}
                              danger
                          >
                              {CommonConstant.REJECT}
                          </Button>,
                      ]
            }
            destroyOnClose
        >
            <MLScoreModalBody loading={loading} mlRow={mlRow} appData={appData} onChangeComment={onChangeComment} />
        </Modal>
    );
};

const MLScoreModalBody = ({ loading, mlRow, appData, onChangeComment }) => {
    const [showDiff, setShowDiff] = useState(true);

    if (loading) {
        return <LoadingContainer />;
    }

    if (!appData) {
        return <Result status='error' title={ErrorMsg.GET_APP_FAILED}></Result>;
    }

    return (
        <>
            <Descriptions size={'small'} column={2} style={{ fontSize: '12px' }} title={MLScoreLabels.appInfo} bordered>
                <Descriptions.Item label={MLScoreLabels.appName}>
                    <div className='ns-flex ns-flex_gap'>
                        <div>{mlRow.Application.current_name}</div>
                        <a target={'_blank'} href={`${UiURLs.RESEARCH_UI}${mlRow.application_id}`}>
                            <ExportOutlined />
                        </a>
                    </div>
                </Descriptions.Item>
                <Descriptions.Item label={MLScoreLabels.appCategory}>
                    {appData?.basicDetails?.ApplicationCategoryAssoc?.ApplicationCategory?.category_name}
                </Descriptions.Item>
                <Descriptions.Item label={MLScoreLabels.appUrl}>
                    <a target={'_blank'} href={appData?.basicDetails?.AppUrl?.url}>
                        {appData?.basicDetails?.AppUrl?.url}
                    </a>
                </Descriptions.Item>
                <Descriptions.Item label={MLScoreLabels.aiExecutionDate}>{new Date(mlRow.updateDate).toDateString()}</Descriptions.Item>
                <Descriptions.Item label={MLScoreLabels.existingCci}>{appData?.cci?.cci}</Descriptions.Item>
                <Descriptions.Item label={MLScoreLabels.aiGeneratedCci}>{mlRow.cci}</Descriptions.Item>
                <Descriptions.Item label={MLScoreLabels.completionStatus}>
                    {appData?.original?.researchData?.completionStatus}
                </Descriptions.Item>
                <Descriptions.Item label={MLScoreLabels.lastReviewedDate}>
                    {new Date(appData?.extraInfo.refreshedDate).toDateString()}
                </Descriptions.Item>
            </Descriptions>
            <div className='ns-flex ns-flex_gap ns-margin-top-mid'>
                <Text size={'small'}>{MLScoreTexts.showDiffOnly}</Text>
                <Switch defaultChecked onChange={() => setShowDiff(!showDiff)} />
            </div>
            <div className='ns-flex ns-flex_vertical'>
                {appData.question.map((quesCat) => (
                    <QuesCategoryBlock quesCat={quesCat} attribute={appData.attribute} showDiff={showDiff} />
                ))}
                <QuesCategoryBlock
                    quesCat={{
                        category_name: CommonConstant.GDPR,
                        category_text: CommonConstant.GDPR,
                        AppIndexQuestions: appData.gdprQuestion,
                    }}
                    attribute={appData.gdprAttribute}
                    showDiff={showDiff}
                />
            </div>
            <div className='ns-flex ns-flex_gap ns-items-center ns-margin-top'>
                <Text size={'small'}>{CommonConstant.COMMENTS}</Text>
                <TextArea maxLength={255} style={{ width: '30%' }} autoSize={{ minRows: 2, maxRows: 6 }} onChange={onChangeComment} />
            </div>
        </>
    );
};

const ValidationFailedNotification = ({ failedAttr }) => {
    return (
        <ul>
            {failedAttr.map((attr) => (
                <li key={`notification_${attr.question}`}>
                    <Text>{`${attr.question} -> ${attr.errorMsg}`}</Text>
                </li>
            ))}
        </ul>
    );
};

export default MLScoreModal;
